import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import de from "date-fns/locale/de";
import Modal from "@/components/modal";
import DropDown from "@/components/forms/inputs/dropDown";
import styles from "@/components/dateTime/dateRangeFilterModal.module.css";
import inlineCalendarStyles from "@/components/dateTime/genericInlineCalendar.module.css";
import { Typography } from "@mui/material";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

registerLocale("de", de);

const dateRangeFocusableSelector = [
  '[role="combobox"]:not([aria-disabled="true"])',
  "select:not([disabled])",
  '[role="option"]:not([aria-disabled="true"]):not(.react-datepicker__day--outside-month)',
].join(", ");

export type DateRangeFilterModalValue = [Date | null, Date | null];
type DateRangeValidationTarget = "filter" | "startDate" | "endDate";

interface DateRangeFilterModalProps {
  open: boolean;
  initialDateRange?: DateRangeFilterModalValue | [];
  initialFilter?: string;
  defaultFilter?: string;
  filterOptions?: { value: any; label: any }[];
  allowSingleDate?: boolean;
  title?: string;
  description?: string;
  onApply: (dateRange: DateRangeFilterModalValue, filter?: string) => void;
  onClose: () => void;
}

const DateRangeFilterModal: FC<DateRangeFilterModalProps> = ({
  open,
  initialDateRange = [],
  initialFilter = "",
  defaultFilter = "",
  filterOptions = [],
  allowSingleDate = false,
  title,
  description,
  onApply,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const initialStartDate = initialDateRange[0] ?? null;
  const initialEndDate = initialDateRange[1] ?? null;
  const resolvedInitialFilter =
    initialFilter || defaultFilter || String(filterOptions[0]?.value ?? "");
  const [dateRange, setDateRange] = useState<DateRangeFilterModalValue>([
    initialStartDate,
    initialEndDate,
  ]);
  const [chosenFilter, setChosenFilter] = useState(resolvedInitialFilter);
  const [validationErrors, setValidationErrors] = useState({
    filter: false,
    startDate: false,
    endDate: false,
  });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      DateRangeValidationTarget
    >({
      fieldOrder: [],
      focusableSelector: dateRangeFocusableSelector,
    });
  const [startDate, endDate] = dateRange;
  const showFilterSelector = filterOptions.length > 0;
  const calendarLocale = i18n.language === "de" ? "de" : undefined;

  useEffect(() => {
    if (!open) return;
    setDateRange([initialStartDate, initialEndDate]);
    setChosenFilter(resolvedInitialFilter);
    setValidationErrors({ filter: false, startDate: false, endDate: false });
  }, [
    open,
    initialStartDate,
    initialEndDate,
    resolvedInitialFilter,
  ]);

  const handleApply = () => {
    const nextErrors = {
      filter: showFilterSelector && !chosenFilter,
      startDate: startDate === null,
      endDate: !allowSingleDate && endDate === null,
    };
    setValidationErrors(nextErrors);

    const invalidTargets: DateRangeValidationTarget[] = [];
    if (nextErrors.filter) invalidTargets.push("filter");
    if (nextErrors.startDate) invalidTargets.push("startDate");
    if (nextErrors.endDate) invalidTargets.push("endDate");
    if (invalidTargets.length > 0) {
      focusFirstInvalidField({}, invalidTargets);
      return;
    }

    onApply(dateRange, showFilterSelector ? chosenFilter : undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? t("apply_filter")}
      hideSubmitButton={false}
      onSubmit={handleApply}
      buttonPlacement="space-between"
      cancelButtonText={t("cancel")}
      submitButtonText={t("apply_filter")}
      contentClassName={styles.modalContent}
      maxWidth="28rem"
      paperBorderRadius="0.75rem"
    >
      <div className={styles.content}>
        {description && <p className={styles.description}>{description}</p>}
        {showFilterSelector && (
          <div
            className={styles.filterSelector}
            ref={registerValidationTarget("filter")}
          >
            <DropDown
              options={filterOptions}
              onChange={(selectedOption: any) => {
                setChosenFilter(selectedOption?.value ?? "");
                setValidationErrors((current) => ({
                  ...current,
                  filter: false,
                }));
              }}
              isClearable={true}
              defaultValue={chosenFilter}
              resetInput={() => setChosenFilter("")}
            />
            {validationErrors.filter && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </div>
        )}
        <div
          ref={(element) => {
            registerValidationTarget("startDate")(element);
            registerValidationTarget("endDate")(element);
          }}
          className={inlineCalendarStyles.surface}
        >
          <DatePicker
            selectsRange={true}
            onChange={(update) => {
              const nextRange = update as DateRangeFilterModalValue;
              setDateRange(nextRange);
              setValidationErrors((current) => ({
                ...current,
                startDate: false,
                endDate: false,
              }));
            }}
            locale={calendarLocale}
            calendarStartDay={1}
            startDate={startDate}
            endDate={endDate}
            calendarClassName={inlineCalendarStyles.calendar}
            peekNextMonth
            inline
            disabledKeyboardNavigation
          />
        </div>
        {(validationErrors.startDate || validationErrors.endDate) && (
          <Typography color="error" variant="caption" role="alert">
            {t("field_required")}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

export default DateRangeFilterModal;
